import { noop } from 'lodash'
import { Fragment, forwardRef, useState } from 'react'
import styled from 'styled-components'
import { colors, fontWeights, transitions } from '@/styles'
import toId from '@/utils/to-id'
import CheckedIcon from './checked.svg'
import Label, { active } from './floating-label'

const Control = styled.input<{ left?: number; round?: boolean; hasError?: boolean }>`
  filter: none;
  height: 48px;
  border-radius: ${props => (props.round ? '50%' : '6px')};
  border: 1px solid ${colors.grey};
  padding: 0 15px;
  font-size: 14px;
  outline: none;
  width: 100%;
  max-width: 100%;
  transition: border-color ${transitions.ease};

  &::placeholder {
    color: #ccc;
    font-weight: ${fontWeights.regular};
  }

  &[type='tel'] {
    border: none;
    background: none;
  }

  &:disabled,
  &[disabled] {
    cursor: default;
    opacity: 0.3;
    &:not([type]),
    &[type=''] {
      cursor: not-allowed;
    }
  }

  &:not([type]),
  &[type=''],
  &[type='text'],
  &[type='code'],
  &[type='tel'],
  &[type='password'],
  &[type='email'] {
    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus {
      transition: background-color 5000s ease-in-out 0s;
      &:not([type='tel']) {
        -webkit-box-shadow: 0 0 0 50px ${colors.white} inset;
      }
    }
    &::placeholder {
      opacity: 0;
    }
  }

  &:not([type]),
  &[type=''],
  &[type='text'],
  &[type='code'],
  &[type='password'],
  &[type='email'] {
    padding: 12px 12px 0 calc(12px + ${props => props.left ?? 0}px);
    background: ${colors.white};
    &:focus {
      border-color: ${colors.secondary};
    }
    ${props =>
      props.hasError &&
      `
      border-color: ${colors.red} !important;
    `}
  }

  &[type='checkbox'],
  &[type='radio'],
  &[type='submit'] {
    cursor: pointer;
  }

  &[type='radio'],
  &[type='checkbox'] {
    position: relative;
    height: 16px;
    width: 16px;
    min-width: 16px;
    padding: 0;
    appearance: none;
    border-color: ${colors.darkGrey};
    border-width: 1px;
    border-radius: 4px;
    background-color: ${colors.white};
    /* ACCESSIBILITY FOCUS OUTLINE */
    :before {
      content: '';
      position: absolute;
      border: none;
    }
    :focus {
      :before {
        top: -6px;
        left: -6px;
        right: -6px;
        bottom: -6px;
        border: 3px solid ${colors.lightBlue};
        border-radius: 8px;
      }
    }
  }

  &[type='radio'] {
    border-radius: 13px;
  }

  &[type='radio']:disabled,
  &[type='checkbox']:disabled {
    cursor: default;
    opacity: 0.3;
  }

  &[type='radio']:checked,
  &[type='checkbox']:checked {
    background-color: ${colors.secondary};
    border-color: ${colors.secondary};
    background-image: url('${CheckedIcon}');
    background-size: 10px 10px;
    background-position: center center;
    background-repeat: no-repeat;
  }
`

const Container = styled.div`
  position: relative;
  input:focus + label {
    ${active}
  }
  input:not(:placeholder-shown) + label {
    ${active}
  }
  input:autofill + label {
    ${active}
  }
  &:-webkit-autofill + label,
  &:-webkit-autofill:hover + label,
  &:-webkit-autofill:focus + label {
    ${active}
  }
`

const Required = styled.span`
  color: ${colors.red64};
`

export type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  hasError?: boolean
  suffix?: string
  prefix?: string
  left?: number
  required?: boolean
  labelY?: number
}

type OnFocus = InputProps['onFocus']
type OnBlur = InputProps['onBlur']

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      type = 'text',
      onFocus = noop,
      onBlur = noop,
      prefix,
      suffix,
      value,
      hasError,
      placeholder,
      id: idInitial,
      left,
      required,
      labelY,
      ...props
    },
    ref
  ) => {
    const [focused, setFocused] = useState(false)

    const handleFocus: OnFocus = e => {
      setFocused(true)
      onFocus(e)
    }
    const handleBlur: OnBlur = e => {
      setFocused(false)
      onBlur(e)
    }
    const getPrefix = () => (prefix ? prefix : '')
    const getSuffix = () => (suffix ? ` ${suffix}` : '')
    const getValue = () => (value && !focused ? `${getPrefix()}${value}${getSuffix()}` : value)
    const Outer = placeholder ? Container : Fragment
    const id = idInitial || toId(placeholder) || undefined
    const labelProps = left ? { x: left + 2 } : {}
    return (
      <Outer>
        <Control
          {...props}
          type={type}
          id={id}
          hasError={hasError}
          placeholder={placeholder}
          ref={ref}
          onFocus={handleFocus}
          onBlur={handleBlur}
          value={getValue()}
          left={left}
        />
        {placeholder && (
          <Label htmlFor={id} y={labelY} {...labelProps}>
            {placeholder}
            {required && <Required>*</Required>}
          </Label>
        )}
      </Outer>
    )
  }
)

Input.displayName = 'Input'

export default Input
